import { ForwardedRef, forwardRef } from "react";
import { isEqual } from "lodash";
import { RetailerType } from "__generated__/gql/graphql";
import { getPrefixedId } from "components/Shared/FilterEditor/createNestedFilterDefinitions";
import { AppliedFilter } from "components/Shared/FilterEditor/utils";
import CheckboxOptionsSelect, {
  CheckboxOptionsSelectProps,
} from "components/Shared/Forms/CheckboxOptionsSelect";

export const RETAILER_TYPE_FILTER_ID = "retailerType";

export const RETAILER_TYPE_OPTIONS = [
  RetailerType.CHAIN,
  RetailerType.INDEPENDENT,
  RetailerType.AFFILIATION,
  RetailerType.OTHER,
];

export const RETAILER_TYPE_LABELS: Record<RetailerType, string> = {
  [RetailerType.CHAIN]: "Chain",
  [RetailerType.INDEPENDENT]: "Independent",
  [RetailerType.AFFILIATION]: "Affiliation",
  [RetailerType.OTHER]: "Other",
};

export function getIsEveryOptionSelected(value: RetailerType[]) {
  const sortedValue = value.slice().sort();
  const sortedOptions = RETAILER_TYPE_OPTIONS.slice().sort();
  return isEqual(sortedValue, sortedOptions);
}

export function getValueFromFilters(filters: AppliedFilter[], prefix?: string) {
  const retailerTypeFilter = filters.find(
    (f) => f.filter === getPrefixedId(prefix, RETAILER_TYPE_FILTER_ID),
  );

  if (!retailerTypeFilter) return RETAILER_TYPE_OPTIONS.slice(0);

  if (!Array.isArray(retailerTypeFilter.value)) return;

  if (retailerTypeFilter.value.length === 0)
    return RETAILER_TYPE_OPTIONS.slice(0);

  if (retailerTypeFilter.variant === "equalTo") {
    const option = RETAILER_TYPE_OPTIONS.find(
      (o) => o === retailerTypeFilter.value,
    );
    return option && [option];
  }

  if (retailerTypeFilter.variant === "notEqualTo") {
    return RETAILER_TYPE_OPTIONS.filter((o) => o !== retailerTypeFilter.value);
  }

  if (retailerTypeFilter.variant === "in") {
    return RETAILER_TYPE_OPTIONS.filter((o) =>
      (retailerTypeFilter.value as unknown[]).includes(o),
    );
  }

  if (retailerTypeFilter.variant === "notIn") {
    return RETAILER_TYPE_OPTIONS.filter(
      (o) => !(retailerTypeFilter.value as unknown[]).includes(o),
    );
  }
}

export function makeFiltersHandleChange(
  existingFilters: AppliedFilter[],
  handleFiltersChange: (filters: AppliedFilter[]) => void,
  prefix?: string,
) {
  const filterId = getPrefixedId(prefix, RETAILER_TYPE_FILTER_ID);
  return (value: RetailerType[]) => {
    const otherFilters = existingFilters.filter((f) => f.filter !== filterId);
    handleFiltersChange(
      getIsEveryOptionSelected(value)
        ? otherFilters
        : [
            ...otherFilters,
            {
              filter: filterId,
              value:
                value.length === 0 ? RETAILER_TYPE_OPTIONS.slice(0) : value,
              variant: "in",
            },
          ],
    );
  };
}

type Props = Omit<CheckboxOptionsSelectProps<RetailerType>, "options">;

function RetailerTypeCheckboxesInner(
  props: Props,
  ref: ForwardedRef<HTMLUListElement>,
) {
  return (
    <CheckboxOptionsSelect<RetailerType>
      ref={ref}
      options={RETAILER_TYPE_OPTIONS}
      getLabel={(o) => `${RETAILER_TYPE_LABELS[o]}s`}
      {...props}
    />
  );
}

export default forwardRef(RetailerTypeCheckboxesInner);
